import React, { useState } from 'react';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useMsal } from "@azure/msal-react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs';

// Configure the worker source for pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

export default function UploadBoth() {
  const initialTimestamp = Date.now();
  const [timestamp, setTimestamp] = useState(initialTimestamp);
  const [fmcsaFile, setFm] = useState(null);
  const [plateFile, setPlate] = useState(null);
  const [uploadClicked, setUploadClicked] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [folder, setFolder] = useState(`Uploads/${initialTimestamp}`);
  const [missingFields, setMissingFields] = useState([]);
  const [originalPayload, setOriginalPayload] = useState(null);
  const navigate = useNavigate();
  const { accounts } = useMsal();

  const [unitInput, setUnitInput] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [mechanicInput, setMechanicInput] = useState('');
  const [facilityInput, setFacilityInput] = useState('');

  const [unitError, setUnitError] = useState('');
  const [dateError, setDateError] = useState('');
  const [facilityError, setFacilityError] = useState('');
  const isValidUnit = (value) => /^[A-Za-z]{4}\d{6}$/.test(value);
  const isValidDate = (value) => /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{2}$/.test(value);

  const [isConverting, setIsConverting] = useState(false);

  // Function to convert PDF to JPG using pdfjs-dist
  const convertPdfToJpg = async (file) => {
    setIsConverting(true);
    try {
      const pdfData = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
      const page = await pdf.getPage(1); // Get the first page
      const viewport = page.getViewport({ scale: 2 });

      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');

      await page.render({ canvasContext: ctx, viewport: viewport }).promise;

      const jpgBlob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
      });

      const jpgFile = new File([jpgBlob], file.name.replace(/\.[^.]+$/, '.jpeg'), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });

      setIsConverting(false);
      return jpgFile;
    } catch (error) {
      // console.error('Error converting PDF to JPEG:', error);
      setIsConverting(false);
      return file;
    }
  };

  const handleFmcsaChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type === 'application/pdf') {
      const convertedFile = await convertPdfToJpg(file);
      setFm(convertedFile);
    } else {
      setFm(file);
    }
  };

  const handlePlateChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type === 'application/pdf') {
      const convertedFile = await convertPdfToJpg(file);
      setPlate(convertedFile);
    } else {
      setPlate(file);
    }
  };

  const handleUpload = async () => {
    if (!fmcsaFile || !plateFile) {
      alert("Please select both FMCSA and Chassis images.");
      return;
    }

    const session = await fetchAuthSession();
    setUploadClicked(true);
    

    // try {
    //   // 1) Upload FMCSA first
    //   await uploadData({
    //     key: `${folder}/FMCSA.jpeg`,
    //     data: fmcsaFile,
    //     options: { accessLevel: 'protected' }
    //   });

    //   // 2) Upload Plate second
    //   await uploadData({
    //     key: `${folder}/Plate.jpeg`,
    //     data: plateFile,
    //     options: { accessLevel: 'protected' }
    //   });

    //   // 3) (Optional) Verify both exist with signed URLs before signaling ready
    //   try {
    //     const [fmcsaUrl, plateUrl] = await Promise.all([
    //       getUrl({ key: `${folder}/FMCSA.jpeg`, options: { accessLevel: 'protected' } }),
    //       getUrl({ key: `${folder}/Plate.jpeg`, options: { accessLevel: 'protected' } }),
    //     ]);
    //     console.log("✅ FMCSA URL:", fmcsaUrl.url);
    //     console.log("✅ Plate URL:", plateUrl.url);
    //   } catch (vErr) {
    //     console.warn("⚠️ Skipping verify step, could not pre-sign URLs:", vErr);
    //   }

    //   // 4) Upload meta.json LAST to signal “ready”
    //   setStatusMessage('Uploading Metadata...');
    //   await uploadData({
    //     key: `${folder}/meta.json`,
    //     data: JSON.stringify({ submittedBy: accounts[0]?.username || 'FMCSA.app@dcli.com' }),
    //     options: { accessLevel: 'protected' }
        
    //   });
    setStatusMessage('Uploading FMCSA...');  
    try {
      // 1) Upload FMCSA first (await true completion)
      const fTask = uploadData({
        key: `${folder}/FMCSA.jpeg`,
        data: fmcsaFile,
        options: {
          accessLevel: 'protected',
          contentType: 'image/jpeg',
        }
      });
      // Optional: progress callback
      // fTask.onProgress(p => console.log('FMCSA progress', p));
      await fTask.result;

      setStatusMessage('Uploading Plate...');

      // 2) Upload Plate second (await true completion)
      const pTask = uploadData({
        key: `${folder}/Plate.jpeg`,
        data: plateFile,
        options: {
          accessLevel: 'protected',
          contentType: 'image/jpeg',
        }
      });
      // pTask.onProgress(p => console.log('Plate progress', p));
      await pTask.result;

      // 3) Verify both exist (HEAD via signed URL)
      try {
        const [fmcsaUrl, plateUrl] = await Promise.all([
          getUrl({ key: `${folder}/FMCSA.jpeg`, options: { accessLevel: 'protected' } }),
          getUrl({ key: `${folder}/Plate.jpeg`, options: { accessLevel: 'protected' } }),
        ]);
        // console.log("✅ FMCSA URL:", fmcsaUrl.url);
        // console.log("✅ Plate URL:", plateUrl.url);
      } catch (vErr) {
        // console.warn("⚠️ Verify step failed (will still proceed):", vErr);
      }

      // 4) Upload meta.json LAST to signal ready (await true completion)
      setStatusMessage('Uploading Metadata...');
      const mTask = uploadData({
        key: `${folder}/meta.json`,
        data: JSON.stringify({ submittedBy: accounts[0]?.username || 'FMCSA.app@dcli.com' }),
        options: {
          accessLevel: 'protected',
          contentType: 'application/json',
        }
      });
    await mTask.result;

    // 5) Now the backend can start; show user progress
    setStatusMessage('Processing...');
    checkStatus();


    } catch (err) {
      // console.error("❌ Upload error:", err);
      setStatusMessage('Upload Failed.');
    }
  };

  const checkStatus = async () => {
    const maxAttempts = 6;
    const delay = 5000;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const statusUrl = await getUrl({
          key: `StatusUploads/${timestamp}/status.json`,
          options: { accessLevel: 'protected' }
        });
        const res = await axios.get(statusUrl.url);

        if (res.data.status === 'success') {
          // Check if chassis validation passed
          if (res.data.is_chassis_valid === false) {
            const chassisNumber = res.data.payload?.unit || 'Unknown';
            setStatusMessage(`⚠️ [${chassisNumber}] Chassis not valid in CMS`);
            return;
          }
          setStatusMessage('✅ Successfully Completed');
          setTimeout(() => navigate('/'), 1500);
          return;
        } else if (res.data.status === 'missing') {
          setOriginalPayload(res.data.payload);
          setMissingFields(res.data.missingFields);
          setStatusMessage('⚠️ Manual Input Required');
          return;
        } else {
          setStatusMessage('⚠️ AI Extraction Failed, manually send the Payload');
          setMissingFields(['unit', 'date', 'facility']);
        }
      } catch (error) {
        // console.log(`Status file not ready (attempt ${attempt})... retrying in ${delay}ms`);
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    setMissingFields(['unit', 'date', 'facility']);
    setStatusMessage('⚠️ AI Extraction Failed, manually send the Payload');
  };

  const submitManual = async () => {
    let valid = true;
    setUnitError('');
    setDateError('');
    setFacilityError('');

    if (missingFields.includes('unit') && !isValidUnit(unitInput.trim())) {
      setUnitError('Unit must be 4 letters followed by 6 digits (e.g. ABCD123456)');
      valid = false;
    }

    if (missingFields.includes('date') && !isValidDate(dateInput.trim())) {
      setDateError('Date must be in MM-DD-YY format (e.g. 04-22-25)');
      valid = false;
    }

    if (missingFields.includes('facility') && facilityInput.trim().length < 3) {
      setFacilityError('Facility must be at least 3 characters long.');
      valid = false;
    }
    
    if (!valid) return;

    const updatedPayload = { ...originalPayload };

    if (missingFields.includes('unit') && unitInput.trim()) {
      updatedPayload.unit = unitInput.trim();
    }
    if (missingFields.includes('date') && dateInput.trim()) {
      updatedPayload.date = dateInput.trim();
    }
    // if (missingFields.includes('mechanic') && mechanicInput.trim()) {
    //   updatedPayload.mechanic = mechanicInput.trim();
    // }
    if (missingFields.includes('facility') && facilityInput.trim()) {
      updatedPayload.facility = facilityInput.trim();
    }

    // Validate chassis before submitting to Lambda 2
    const chassisToValidate = updatedPayload.unit;
    if (chassisToValidate) {
      setStatusMessage('Validating chassis...');
      const valEndpoint = process.env.REACT_APP_VAL_API_ENDPOINT;
      const valKey = process.env.REACT_APP_VAL_API_KEY;
      
//       console.log('🔍 Chassis Validation Attempt:');
//       console.log('  Chassis:', chassisToValidate);
//       console.log('  Endpoint:', valEndpoint);
      // console.log('  API Key:', valKey ? `${valKey.substring(0, 8)}...` : 'MISSING');
      try {
        const validationResponse = await axios.get(
          `${process.env.REACT_APP_API_ENDPOINT}/validate-chassis/${chassisToValidate}`,
          {
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY
            },
            timeout: 10000
          }
        );

        // console.log('✅ Validation response:', validationResponse.data);
        // Check if chassis is valid (has unitNumber in response)
        if (!validationResponse.data?.isValid) {
          setStatusMessage(`⚠️ [${chassisToValidate}] Chassis not valid in CMS`);
          setUnitError(validationResponse.data.message || `Chassis ${chassisToValidate} not found in system.`);
          return;
        }

        // Chassis is valid, proceed with submission
        // console.log(`✅ Chassis ${chassisToValidate} validated successfully`);
        
      } catch (error) {
        // console.error('❌ Chassis Validation Error:');
        // console.error('  Response data:', error.response?.data);
        setStatusMessage(`⚠️ [${chassisToValidate}] Chassis not valid in CMS`);
        // setUnitError(`Chassis ${chassisToValidate} not found in system. Please add it to CMS first.`);
        return;
      }
    }

    // await post({
    //   apiName: "submit-correction",
    //   path: "/submit-correction",
    //   options: {
    //     body: updatedPayload,
    //     headers: {
    //       'Content-Type': 'application/json'
    //     }
    //   }
    // });

    try {
      // Get authentication session
      const session = await fetchAuthSession();
      // console.log("🔐 Session:", session);
      
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_API_KEY //'tGSDkmii0G1fan9gJtRG2vBxPnCRFaE96Xa3shmg'
      };
      // console.log("🔑 API Key from env:", process.env.REACT_APP_API_KEY);
      // console.log("🌐 API Gateway URL:", process.env.REACT_APP_API_ENDPOINT);

      // Add authorization header if available
      // if (session.tokens?.idToken) {
      //   headers['Authorization'] = `Bearer ${session.tokens.idToken.toString()}`;
      // } else if (session.tokens?.accessToken) {
      //   headers['Authorization'] = `Bearer ${session.tokens.accessToken.toString()}`;
      // }

      // console.log("📋 Headers:", headers);
      // console.log("📦 Payload:", updatedPayload);

      const response = await axios.post(
         `${process.env.REACT_APP_API_ENDPOINT}/submit-correction`, //'https://qthk56ojb1.execute-api.us-east-2.amazonaws.com/dev/submit-correction',
        updatedPayload,
        { headers }
      );    
      
      // console.log("✅ API Response received:");
      // console.log("📊 Response status:", response.status);
      // console.log("📄 Response data:", response.data);
      // console.log("✅ Data sent to Lambda 2");
      
      setStatusMessage('✅ Successfully Completed');
      setMissingFields([]);
      setTimeout(() => navigate('/'), 1500);
      
    } catch (error) {
      // console.error("❌ API call failed:");
      // console.error("Error:", error);
      // console.error("Response:", error.response?.data);
      // console.error("Status:", error.response?.status);
      
      setStatusMessage('❌ Failed to submit correction');
    }
  };

  const renderPreview = (file) =>
    file ? (
      <img
        src={URL.createObjectURL(file)}
        alt="preview"
        style={{ width: 300, marginTop: 10, border: '2px solid #ccc' }}
      />
    ) : null;

  return (
    <div style={{ textAlign: 'center', padding: 50 }}>
      <h1>Upload FMCSA Form & Chassis Image</h1>

      <h3>FMCSA Form:</h3>
      <input
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFmcsaChange}
        disabled={isConverting}
      /><br />
      {renderPreview(fmcsaFile)}<br /><br />

      <h3>Chassis Image:</h3>
      <input
        type="file"
        accept="image/*,application/pdf"
        onChange={handlePlateChange}
        disabled={isConverting}
      /><br />
      {renderPreview(plateFile)}<br /><br />

      <button
        onClick={handleUpload}
        disabled={uploadClicked || isConverting}
        style={{
          padding: 10,
          fontSize: 18,
          backgroundColor: (uploadClicked || isConverting) ? '#bdc3c7' : '#3498db',
          color: 'white',
          cursor: (uploadClicked || isConverting) ? 'not-allowed' : 'pointer'
        }}
      >
        Submit Both
      </button>

      <div style={{ marginTop: 20, fontSize: 22 }}>
        {statusMessage}
      </div>

      {missingFields.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <h3>Provide Missing Details:</h3>

          {missingFields.includes('unit') && (
            <>
              <label>Unit Number:</label><br />
              <input
                type="text"
                value={unitInput}
                onChange={(e) => setUnitInput(e.target.value)}
                style={{ marginBottom: 10 }}
              /><br />
              {unitError && <span style={{ color: 'red' }}>{unitError}</span>}<br />
            </>
          )}

          {missingFields.includes('date') && (
            <>
              <label>Date (MM-DD-YY):</label><br />
              <input
                type="text"
                value={dateInput}
                onChange={(e) => setDateInput(e.target.value)}
                style={{ marginBottom: 10 }}
              /><br />
              {dateError && <span style={{ color: 'red' }}>{dateError}</span>}<br />
            </>
          )}

          {missingFields.includes('mechanic') && (
            <>
              <label>Mechanic Name:</label><br />
              <input
                type="text"
                value={mechanicInput}
                onChange={(e) => setMechanicInput(e.target.value)}
                style={{ marginBottom: 25 }}
              /><br />
            </>
          )}

          {missingFields.includes('facility') && (
            <>
              <label>Facility:</label><br />
              <input
                type="text"
                value={facilityInput}
                onChange={(e) => setFacilityInput(e.target.value)}
                style={{ marginBottom: 10 }}
              /><br />
              {facilityError && <span style={{ color: 'red' }}>{facilityError}</span>}<br />
            </>
          )}

          <button
            onClick={submitManual}
            style={{
              padding: 10,
              fontSize: 18,
              backgroundColor: '#2ecc71',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Complete Manually
          </button>
        </div>
      )}
    </div>
  );
}
